import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { useApiClient } from '../../auth/hooks/use-api-client.hook';
import { DocumentType, ResourceType } from '../core/types';
import { useResourceEndpoints } from '../core/use-resource-endpoints.hook';
import { toClearEndpointName, toSetEndpointName } from '../utils/resource-field-mutations.utils';
import { addResponseFields, handleResourceError } from '../utils/resource-hooks.utils';

export interface SetFieldsConfig {
	/** The resource type */
	resourceType: ResourceType | DocumentType;
	/** Fields to return in response */
	responseFields?: string[];
	/** Whether to use /update endpoint (true) or multiple /call/setField calls (false) */
	useUpdateEndpoint?: boolean;
}

export interface SetFieldsVariables {
	/** The resource ID */
	resourceId: string;
	/** Field name -> value mapping */
	fields: Record<string, unknown>;
}

/**
 * Configuration for mutating a system field on a resource.
 */
export interface SystemFieldConfig {
	/** The resource type (e.g., 'reservations', 'checkouts') */
	resourceType: ResourceType | DocumentType;
	/** The field name in camelCase (e.g., 'name', 'location', 'customer') */
	fieldName: string;
	/** Whether to pass the value as a query parameter (true) or in the request body (false) */
	useQueryParam?: boolean;
	/** Optional Python field name if different from fieldName (e.g., 'customer' for 'contactId') */
	pythonFieldName?: string;
	/** Optional custom set endpoint name if different from set{FieldName} (e.g., 'setCustomer' for 'contact') */
	setEndpointName?: string;
	/** Optional custom clear endpoint name if different from clear{FieldName} (e.g., 'clearCustomer' for 'contact') */
	clearEndpointName?: string;
	/** HTTP method for clear operation (default: 'POST') */
	clearMethod?: 'GET' | 'POST';
	/** Additional fields to include in the request body (e.g., { _fields: '_id' }) */
	additionalBodyFields?: Record<string, string>;
}

export interface SystemFieldResponse {
	_id: string;
}

export interface SetSystemFieldVariables {
	resourceId: string;
	value: string | null;
}

export interface ClearSystemFieldVariables {
	resourceId: string;
}

export interface SetOrClearSystemFieldVariables {
	resourceId: string;
	value: string | null;
}

/**
 * Generic hook for setting a system field on a resource.
 *
 * @example
 * const { mutateAsync: setName } = useSetSystemFieldMutation({
 *   resourceType: 'reservations',
 *   fieldName: 'name',
 *   useQueryParam: false
 * });
 * await setName({ resourceId: '123', value: 'My Reservation' });
 *
 * Note: For contact mutations with null handling, use useSetResourceContactMutation from
 * '../../system-field-mutations' instead.
 */
export const useSetSystemFieldMutation = <TError = unknown, TContext = unknown>(
	config: SystemFieldConfig,
	options?: UseMutationOptions<SystemFieldResponse, TError, SetSystemFieldVariables, TContext>
) => {
	const apiClient = useApiClient();
	const pythonFieldName = config.pythonFieldName || config.fieldName;
	const endpoints = useResourceEndpoints(config.resourceType);

	return useMutation<SystemFieldResponse, TError, SetSystemFieldVariables, TContext>(
		async ({ resourceId, value }) => {
			if (value === null || value === undefined) {
				throw new Error(`Cannot set ${config.fieldName} to null. Use clear mutation instead.`);
			}

			try {
				const setFieldName = config.setEndpointName || toSetEndpointName(config.fieldName);

				let url: string;
				let body: Record<string, string | null> = {};

				if (config.useQueryParam) {
					url = endpoints.getUrl(resourceId, `/${setFieldName}`, { [pythonFieldName]: value });
					body = {};
				} else {
					url = endpoints.getUrl(resourceId, `/${setFieldName}`);
					body = {
						[pythonFieldName]: value,
						...(config.additionalBodyFields || {}),
					};
				}

				return await apiClient.post<SystemFieldResponse>(url, body);
			} catch (error) {
				throw handleResourceError(error);
			}
		},
		options
	);
};

/**
 * Hook for clearing a system field on a resource.
 *
 * @example
 * const { mutateAsync: clearName } = useClearSystemFieldMutation({
 *   resourceType: 'reservations',
 *   fieldName: 'name',
 *   clearMethod: 'GET'
 * });
 * await clearName({ resourceId: '123' });
 */
export const useClearSystemFieldMutation = <TError = unknown, TContext = unknown>(
	config: SystemFieldConfig,
	options?: UseMutationOptions<SystemFieldResponse, TError, ClearSystemFieldVariables, TContext>
) => {
	const apiClient = useApiClient();
	const endpoints = useResourceEndpoints(config.resourceType);

	return useMutation<SystemFieldResponse, TError, ClearSystemFieldVariables, TContext>(async ({ resourceId }) => {
		try {
			const clearFieldName = config.clearEndpointName || toClearEndpointName(config.fieldName);
			const url = endpoints.getUrl(resourceId, `/${clearFieldName}`);
			const method = config.clearMethod || 'GET';
			const body =
				config.additionalBodyFields && method === 'POST' && Object.keys(config.additionalBodyFields).length > 0
					? config.additionalBodyFields
					: undefined;

			const postOrGet = method === 'POST' ? apiClient.post : apiClient.get;
			return await postOrGet<SystemFieldResponse>(url, body);
		} catch (error) {
			throw handleResourceError(error);
		}
	}, options);
};

/**
 * Hook for setting or clearing a system field on a resource.
 *
 * This hook uses a single internal mutation to avoid race conditions when
 * rapidly calling set/clear operations. It handles both set and clear logic
 * internally rather than delegating to separate mutation hooks.
 *
 * @example
 * const { mutateAsync: setNameOrClear } = useSetOrClearSystemFieldMutation({
 *   resourceType: 'reservations',
 *   fieldName: 'name',
 *   useQueryParam: false
 * });
 * await setNameOrClear({ resourceId: '123', value: 'My Reservation' });
 * await setNameOrClear({ resourceId: '123', value: null });
 */
export const useSetOrClearSystemFieldMutation = <TError = unknown, TContext = unknown>(
	config: SystemFieldConfig,
	options?: UseMutationOptions<SystemFieldResponse, TError, SetOrClearSystemFieldVariables, TContext>
) => {
	const apiClient = useApiClient();
	const pythonFieldName = config.pythonFieldName || config.fieldName;
	const endpoints = useResourceEndpoints(config.resourceType);

	return useMutation<SystemFieldResponse, TError, SetOrClearSystemFieldVariables, TContext>(
		async ({ resourceId, value }) => {
			try {
				// Handle clear operation
				if (value === null || value === undefined) {
					const clearFieldName = config.clearEndpointName || toClearEndpointName(config.fieldName);
					const url = endpoints.getUrl(resourceId, `/${clearFieldName}`);
					const method = config.clearMethod || 'GET';
					const body =
						config.additionalBodyFields &&
						method === 'POST' &&
						Object.keys(config.additionalBodyFields).length > 0
							? config.additionalBodyFields
							: undefined;

					const postOrGet = method === 'POST' ? apiClient.post : apiClient.get;
					return await postOrGet<SystemFieldResponse>(url, body);
				}

				// Handle set operation
				const setFieldName = config.setEndpointName || toSetEndpointName(config.fieldName);

				let url: string;
				let body: Record<string, string | null> = {};

				if (config.useQueryParam) {
					url = endpoints.getUrl(resourceId, `/${setFieldName}`, { [pythonFieldName]: value });
					body = {};
				} else {
					url = endpoints.getUrl(resourceId, `/${setFieldName}`);
					body = {
						[pythonFieldName]: value,
						...(config.additionalBodyFields || {}),
					};
				}

				return await apiClient.post<SystemFieldResponse>(url, body);
			} catch (error) {
				throw handleResourceError(error);
			}
		},
		options
	);
};

/**
 * Hook for setting multiple fields on a resource at once.
 *
 * This can use either:
 * - The `/update` endpoint (if useUpdateEndpoint is true) - single API call
 * - Multiple `/call/setField` calls (if useUpdateEndpoint is false) - multiple API calls
 *
 * @example
 * ```tsx
 * const { mutateAsync: setFields } = useSetFieldsMutation({
 *   resourceType: ResourceType.TICKETS,
 *   useUpdateEndpoint: true,
 *   responseFields: ['_id', 'name', 'status']
 * });
 *
 * await setFields({
 *   resourceId: '123',
 *   fields: {
 *     name: 'Updated Name',
 *     priority: 'high',
 *     'fields.customField': 'value'
 *   }
 * });
 * ```
 */
export const useSetFieldsMutation = <TError = unknown, TContext = unknown>(
	config: SetFieldsConfig,
	options?: UseMutationOptions<SystemFieldResponse, TError, SetFieldsVariables, TContext>
) => {
	const apiClient = useApiClient();
	const endpoints = useResourceEndpoints(config.resourceType);

	return useMutation<SystemFieldResponse, TError, SetFieldsVariables, TContext>(async ({ resourceId, fields }) => {
		if (!fields || Object.keys(fields).length === 0) {
			throw new Error('At least one field must be provided');
		}

		try {
			const body = addResponseFields({ ...fields }, config.responseFields);

			if (config.useUpdateEndpoint !== false) {
				const url = endpoints.getUpdateUrl(resourceId);
				return await apiClient.post<SystemFieldResponse>(url, body);
			} else {
				const fieldUpdates = Object.entries(fields)
					.filter(([, value]) => value !== null && value !== undefined)
					.map(async ([fieldName, value]) => {
						const setFieldName = toSetEndpointName(fieldName);
						const url = endpoints.getCallUrl(resourceId, setFieldName);

						return await apiClient.post<SystemFieldResponse>(url, { [fieldName]: value });
					});

				const responses = await Promise.all(fieldUpdates);

				return responses[responses.length - 1] || { _id: resourceId };
			}
		} catch (error) {
			throw handleResourceError(error);
		}
	}, options);
};
